import React, { type JSX } from 'react';
import { Chip, Icon } from '@saviynt/design-system';

import { P, useInvertedLocalize } from '../../../utilities';
import { type PendingCertification } from '../models';

import { invertedMessages } from './messages';
import RecentAssignmentRow from './RecentAssignmentRow';
import {
  type ConvertToUtc,
  type RecentAssignmentsInvertedLocalize,
} from './types';

import './CertificationRow.css';

interface Props {
  cert: PendingCertification;
  convertToUtc: ConvertToUtc;
}

/**
 * Wrapper around RecentAssignmentRow for a certification.
 */
export default function CertificationRow({
  cert,
  convertToUtc,
}: Props): JSX.Element {
  const l = useInvertedLocalize(invertedMessages);

  return (
    <RecentAssignmentRow
      className='Nav_CertificationRow'
      icon={<Icon kind='Certification' size='large' color='blue-500' />}
      sourceAndPurpose={
        <div className='Nav_CertificationRow-sourceAndPurpose'>
          <P
            variant={2}
            bold
            className='Nav_CertificationRow-sourceAndPurposeHeading'>
            {l('Certification')}
          </P>
          <P variant={2} className='Nav_CertificationRow-sourceAndPurposeId'>
            {cert.id}
          </P>
        </div>
      }
      details={
        <div className='Nav_CertificationRow-details'>
          <P variant={3} className='Nav_CertificationRow-detailsLabel'>
            {l('Type')}
          </P>
          <P variant={2} className='Nav_CertificationRow-detailsValue'>
            {cert.type}
          </P>
        </div>
      }
      subject1={
        <div className='Nav_CertificationRow-subject1'>
          <P variant={3} className='Nav_CertificationRow-subject1Label'>
            {l('Progress')}
          </P>
          <P variant={2} className='Nav_CertificationRow-subject1Value'>
            {cert.progress}
          </P>
        </div>
      }
      subject2={
        <div className='Nav_CertificationRow-subject2'>
          <P variant={3} className='Nav_CertificationRow-subject2Label'>
            {l('Ends at')}
          </P>
          <P variant={2} className='Nav_CertificationRow-subject2Value'>
            {convertToUtc(cert.enddate)}
          </P>
        </div>
      }
      assignmentStatus={assignmentStatus(l, cert.dueIn)}
    />
  );
}

function assignmentStatus(
  l: RecentAssignmentsInvertedLocalize,
  dueIn: string
): JSX.Element | undefined {
  if (dueIn === 'OverDue') {
    return (
      <Chip
        size='small'
        kind='subtleFilled'
        color='critical'
        label={l('Overdue')}
      />
    );
  }

  if (dueIn === 'Complete') {
    return (
      <Chip
        size='small'
        kind='subtleFilled'
        color='success'
        label={l('Complete')}
      />
    );
  }

  const numericMatches = dueIn.match(/\d+/);

  if (!numericMatches) {
    return undefined;
  }

  const dayCount = Number(numericMatches[0]);

  return (
    <Chip
      kind='subtleFilled'
      color={dayCount <= 1 ? 'critical' : 'brandSecondary'}
      label={l('Due in {count} d', { count: dayCount })}
    />
  );
}
